/* ============================================================
   SAM-199 — Access flow as a full page, replacing the AddInvitation drawer.

   Same shell idiom as ServiceEditorPage (src/pages/ServiceEditor/ServiceEditorPage.tsx):
   page header with a back arrow, content scroll area, no baseui/styletron-react
   (per the active BaseUI/Styletron freeze, SAM-175) — this file is presentation
   and routing only.

   All step components (AccessForm, DocumentScanForm, PaymentStep,
   InvitationDetail, ConfirmationView), the ProgressBar, the BookingSummaryRail and
   every payment/3DS/orchestration handler below are carried over UNCHANGED from the
   old AddInvitation.tsx (now removed) — same payloads, same context writes, same
   idempotency-key handling. Only the drawer wrapper became a route + page shell.
   ============================================================ */
import { useEffect, useRef, useState } from "react";
import { useIsPhone } from "../../hooks/useIsPhone";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, UserPlus } from "lucide-react";
import { NotificationService } from "../../services/notification";
import { formatToE164, isValidE164 } from "../../helpers/phoneUtils";
import AccessForm from "./components/AccessForm";
import { DocumentScanForm } from "./components/DocumentScanForm";
import { InvitationDetail } from "./components/InvitationDetail";
import { passWindow } from "./passDates";
import { getApiErrorMessage } from "../../utils/apiErrors";
import { InvitationProvider, useInvitationContext } from "./context/InvitationContext";
import { ProgressBar } from "../../components/ProgressBar";
import { useCreateInvitationOrAccess } from "../../hooks/useInvitationMutations";
import { PaymentStep } from "./components/PaymentStep";
import { PhoneReviewPage } from "./components/PhoneReviewPage";
import { useT } from "../../i18n/admin";
import { fetchUserAttributes, getCurrentUser } from "../../utils/session";
import { SuccessModal } from "../../components/SuccessModal";
import { ConfirmationView } from "./components/ConfirmationView";
import { readHikPin, hikPinFailureMessage } from "./hikPinStatus";
import { DocumentIdData, PassportData, DriverLicenseData } from "../../types/document";
import { useBusinessContext } from "../../context/BusinessContext";
import { ThreeDSModal } from "../../components/ThreeDSModal";
import { PortalApiService, BookingPaymentService } from "../../services/portalApiService";
import { directApiInstance } from "../../api/httpClient";
import { BookingSummaryRail } from "./components/BookingSummaryRail";
import {
  getOrCreateIdempotencyKey,
  buildOnBehalfOf,
  buildOrchestratePayload,
  mapOrchestrationError,
  type OrchestrateAuthData,
} from "./components/serviceBookingOrchestration";
import { isPassFeeService } from "./components/serviceBooking";
import { runIfIdle } from "./inFlight";
import { buildBusinessGuests, scanFields } from './passGuests';
import { useUserPermissions } from "../../hooks/useUserPermissions";
import { picksPassBusiness, resolveBookingBusiness, resolveBusinessAccessTypeId, resolvePassBusiness } from "./invitationBusiness";

enum View {
  FORM = "FORM",
  SCAN = "SCAN",
  PAYMENT = "PAYMENT",
  DETAIL = "DETAIL",
  COMPLETED = "COMPLETED",
}

interface Guest {
  firstName: string;
  lastName: string;
  documentData?: DocumentIdData | PassportData | DriverLicenseData;
  phoneNumber?: string;
}

// SAM-199: this page is a standalone route (/invitations/new), no longer nested inside
// Invitations.tsx's own <InvitationProvider> the way the drawer was — every step component
// below (AccessForm, DocumentScanForm, PaymentStep, InvitationDetail, ConfirmationView,
// BookingSummaryRail) reads/writes the draft via useInvitationContext(), so this route needs
// its own provider instance. resetInvitation() on mount (below) already assumed a fresh draft
// per page-enter; a fresh provider instance per mount is the same assumption at the context level.
export const AccessPage = () => (
  <InvitationProvider>
    <AccessPageContent />
  </InvitationProvider>
);

const AccessPageContent = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isPhone = useIsPhone();
  const { t } = useT();
  const [view, setView] = useState<View>(View.FORM);
  const [showMainGuestModal, setShowMainGuestModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successKind, setSuccessKind] = useState<'pass' | 'booking'>('pass');
  const [isLoading, setIsLoading] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [createdInvitationData, setCreatedInvitationData] = useState<any>(null);
  const [companionsData, setCompanionsData] = useState<{ adults: number; childrens: number }>({ adults: 1, childrens: 0 });
  const [paymentError, setPaymentError] = useState<string | null>(null);
  // What the server answered when it refused to create the pass (400 multi_day_not_allowed, a limit, ...): shown on
  // the Confirm screen instead of only in the console.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [show3DSModal, setShow3DSModal] = useState(false);
  const [threeDSData, setThreeDSData] = useState<{ stepUpUrl: string; accessToken: string; authTransactionId: string } | null>(null);
  // SAM-199: which 3DS chain is currently driving ThreeDSModal — the product flow
  // (processPortalPayment/handle3DSComplete's original branch, PortalApiService) or the
  // service-booking flow (processServiceBookingAuth below, BookingPaymentService). The modal
  // itself is reused unchanged; only which validate/resolve pair handle3DSComplete calls differs.
  const [threeDSFlow, setThreeDSFlow] = useState<'product' | 'booking'>('product');
  const { business } = useBusinessContext();
  const permissions = useUserPermissions();

  const { invitation, updateInvitation, resetInvitation } = useInvitationContext();
  // Client Booking (product or service): the business picked as destination, not the context business, which
  // the tenant Front Desk does not have (SAM-769).
  const bookingBusiness = resolveBookingBusiness({
    destination: invitation?.location?.estate_id?.[0] as any,
    contextBusiness: business ? { id: business.id, name: business.name } : null,
  });
  const { mutateAsync: createInvitation } = useCreateInvitationOrAccess();

  // Reset invitation state once, on entering this page — equivalent to the old
  // drawer's `isOpen` transitioning to true. This page only ever mounts fresh
  // (there is no "reopen without navigating away" case for a route).
  useEffect(() => {
    resetInvitation();
    setView(View.FORM);
    setShowSuccessModal(false);
    setCreatedInvitationData(null);
    setPaymentError(null);
    setShow3DSModal(false);
    setThreeDSData(null);
    setThreeDSFlow('product');
    setCompanionsData({ adults: 1, childrens: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaving this page always used to refresh the Invitations list (the old drawer's
  // onClose called Invitations.tsx's handleClose, which called refetch()
  // unconditionally). The service-booking submit path below posts directly via
  // directApiInstance, not through useCreateInvitationOrAccess, so it does not
  // invalidate the list's query cache on its own — invalidate explicitly here so a
  // freshly booked/created access shows up immediately instead of waiting out
  // staleTime on the list's next mount.
  const goToInvitations = () => {
    queryClient.invalidateQueries({ queryKey: ['business-access-invitations'] });
    queryClient.invalidateQueries({ queryKey: ['invitations'] });
    navigate('/invitations');
  };

  // Dirty-state guard for the back arrow / beforeunload, mirroring
  // ServiceEditorPage's pattern (see that file for why beforeunload is needed:
  // the app uses a plain BrowserRouter, not a data router, so useBlocker isn't
  // available). Nothing to lose once the flow reached COMPLETED.
  const isDirty = view !== View.COMPLETED && (
    view !== View.FORM ||
    Boolean(invitation?.accessType?.length) ||
    Boolean(invitation?.location?.estate_id?.length) ||
    Boolean(invitation?.product?.length) ||
    Boolean(invitation?.serviceBooking) ||
    Boolean(invitation?.guests?.mainGuest?.firstName)
  );

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleBack = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Leave without saving?')) {
      return;
    }
    goToInvitations();
  };

  const handleInvitationCreated = (companionsInfo?: { adults: number; childrens: number }) => {
    setIsFormLoading(true);
    if (companionsInfo) {
      setCompanionsData(companionsInfo);
    }

    // Always navigate to SCAN step - user can choose to scan document or input manually
    setView(View.SCAN);

    // Clear loading state after a short delay to allow view transition
    setTimeout(() => setIsFormLoading(false), 100);
  };

  const handleDocumentsScan = (guest: Guest) => {
    // If the guest has document data from analysis, use it to update invitation
    if (guest.documentData) {
      const documentData = guest.documentData;

      // Extract existing data safely with proper type checking
      const existingMainGuest = invitation?.guests?.mainGuest || {
        firstName: '',
        lastName: '',
        phoneNumber: '',
        idType: [],
        idNumber: ''
      };

      updateInvitation({
        // Explicitly preserve ALL existing invitation data
        ...invitation,
        guests: {
          ...invitation?.guests,
          mainGuest: {
            firstName: documentData.name,
            lastName: documentData.LastName,
            phoneNumber: guest.phoneNumber || existingMainGuest.phoneNumber,
            idNumber: documentData.number,
            idType: existingMainGuest.idType,
            // New document fields for backend storage
            idnum: documentData.number || null,
            nationality: (documentData as any)?.nationality || null,
            birthdate: documentData.DOB || null,
          }
        }
      });
    }

    // No cambiamos la vista aquí ya que DocumentScanForm controlará la navegación
  };

  const handleSendInvitation = () => {
    if (invitation) {
      // Check if payment is required (Client Booking + paid product, or SAM-199 service booking —
      // see the main `requiresPayment` const below for why a service booking always routes
      // through Payment).
      const requiresPayment =
        invitation?.accessType?.[0]?.label === 'Client Booking' &&
        Boolean(
          (invitation?.product?.[0]?.price && invitation.product[0].price > 0) ||
          invitation?.serviceBooking
        );

      // Navigate to payment step if required, otherwise go to detail
      setView(requiresPayment ? View.PAYMENT : View.DETAIL);
    }
  };

  const handleMainGuestSubmit = (data: { firstName: string; lastName: string; phoneNumber: string }) => {
    // DEPRECATED - This function is no longer used since modal is removed
    // Keeping it for backwards compatibility but it should not be called
    if (invitation) {
      updateInvitation({
        // Explicitly preserve all existing fields
        ...invitation,
        guests: {
          ...invitation.guests,
          mainGuest: {
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber,
            idType: [],
            idNumber: "",
          },
          companions: {
            adults: companionsData.adults,
            childrens: companionsData.childrens,
            companions: invitation.guests?.companions?.companions || []
          }
        }
      });

      setShowMainGuestModal(false);

      // Check if payment is required (Client Booking + paid product)
      const requiresPayment =
        invitation?.accessType?.[0]?.label === 'Client Booking' &&
        invitation?.product?.[0]?.price &&
        invitation.product[0].price > 0;

      // Navigate to payment step if required, otherwise go to detail
      setView(requiresPayment ? View.PAYMENT : View.DETAIL);
    }
  };

  // SAM-199 — service-booking 3DS chain (setup -> collect device data -> check enrollment ->
  // [step-up] -> validate), mirroring processPortalPayment above but against the BOOKING lambda's
  // /bookings/payment/{setup,enroll,validate} (BookingPaymentService) instead of /portal
  // (PortalApiService), because the final charge is made server-side by
  // orchestrate_booking_payment, not by a processFinalPayment call from here. Returns the
  // snake_case auth_data to attach to the orchestrate request — never a "payment already
  // processed" result, unlike processPortalPayment: the booking lambda charges the card itself,
  // atomically with creating the hold/booking, once handleServiceBookingConfirm posts auth_data.
  const processServiceBookingAuth = async (params: {
    serviceId: string;
    amount: number;
    currency: string;
  }): Promise<{ success: boolean; authData?: OrchestrateAuthData; error?: string }> => {
    try {
      const creditCard = invitation?.creditCard;
      if (!creditCard) return { success: false, error: 'Credit card information is missing' };
      const [expiryMonth, expiryYear] = creditCard.expiryDate.split('/');
      const card = {
        number: creditCard.cardNumber,
        expiration_month: expiryMonth,
        expiration_year: `20${expiryYear}`,
        security_code: creditCard.cvv,
      };
      const mainGuestPhone = invitation.guests?.mainGuest?.phoneNumber || '';
      const billingAddress = {
        first_name: invitation.guests?.mainGuest?.firstName || creditCard.cardHolder.split(' ')[0] || 'Guest',
        last_name: invitation.guests?.mainGuest?.lastName || creditCard.cardHolder.split(' ').slice(1).join(' ') || '',
        email: mainGuestPhone ? `${mainGuestPhone}@temp.com` : 'guest@temp.com',
        phone_number: mainGuestPhone || '8091234567',
        country: 'DO',
        locality: bookingBusiness.name || 'Punta Cana',
        postal_code: '23000',
        administrative_area: 'La Altagracia',
        address1: 'Bavaro',
      };

      const setup = await BookingPaymentService.setupPayerAuth({
        serviceId: params.serviceId,
        amount: params.amount,
        currency: params.currency,
        card,
        billingAddress,
        merchantName: bookingBusiness.name,
      });

      await BookingPaymentService.collectDeviceData(setup.device_data_collection_url, setup.access_token);

      const enrollment = await BookingPaymentService.checkEnrollment({
        serviceId: params.serviceId,
        referenceId: setup.reference_id,
        amount: params.amount,
        currency: params.currency,
        card,
        billingAddress,
        merchantName: bookingBusiness.name,
        merchantReference: setup.merchant_reference,
      });

      if (enrollment.status === 'AUTHENTICATION_SUCCESSFUL') {
        return { success: true, authData: enrollment.auth_data };
      }

      if (enrollment.status === 'PENDING_AUTHENTICATION' && enrollment.step_up_url && enrollment.access_token) {
        setThreeDSFlow('booking');
        setThreeDSData({
          stepUpUrl: enrollment.step_up_url,
          accessToken: enrollment.access_token,
          authTransactionId: enrollment.authentication_transaction_id || '',
        });
        setShow3DSModal(true);
        return new Promise((resolve) => {
          (window as any).__bookingPaymentResolve = resolve;
          (window as any).__bookingPaymentContext = {
            serviceId: params.serviceId,
            amount: params.amount,
            currency: params.currency,
            card,
            merchantReference: setup.merchant_reference,
          };
        });
      }

      return { success: false, error: `Unexpected 3DS enrollment state: ${enrollment.status}` };
    } catch (error: any) {
      return { success: false, error: error.message || '3DS authentication failed' };
    }
  };

  // Handle 3DS step-up completion
  // 'transactionId' is the Cardinal TransactionId from /3ds-complete.html postMessage
  const handle3DSComplete = async (transactionId: string) => {
    setShow3DSModal(false);
    setThreeDSData(null);

    // SAM-199: the service-booking chain resolves/rejects its own promise via
    // __bookingPaymentResolve — completely separate from the product flow's
    // __portal3DSResolve below, which this branch must not touch.
    if (threeDSFlow === 'booking') {
      const ctx = (window as any).__bookingPaymentContext;
      const resolve = (window as any).__bookingPaymentResolve;
      try {
        if (!ctx) throw new Error('Missing booking payment context');
        const validation = await BookingPaymentService.validateAuthentication({
          serviceId: ctx.serviceId,
          transactionId,
          amount: ctx.amount,
          currency: ctx.currency,
          card: ctx.card,
          merchantReference: ctx.merchantReference,
        });
        if (resolve) resolve({ success: true, authData: validation.auth_data });
      } catch (error: any) {
        if (resolve) resolve({ success: false, error: error.message || '3DS validation failed' });
      } finally {
        delete (window as any).__bookingPaymentResolve;
        delete (window as any).__bookingPaymentContext;
      }
      return;
    }

    try {
      const paymentInfo = (window as any).__portalPaymentInfo;
      if (!paymentInfo) throw new Error('Missing payment information');

      // Step 5: Validate Authentication Results (use Cardinal TransactionId from postMessage)
      const validationResponse = await PortalApiService.validateAuthentication(transactionId, paymentInfo);

      if (validationResponse.status !== 'AUTHENTICATION_SUCCESSFUL') {
        throw new Error(`Authentication validation failed: ${validationResponse.status}`);
      }

      // Extract 3DS auth data from validate response for the final payment
      const authInfo = validationResponse.consumerAuthenticationInformation;
      const authData = {
        cavv: authInfo?.cavv,
        eciRaw: authInfo?.eciRaw,
        eci: authInfo?.eci,
        paresStatus: authInfo?.paresStatus,
        xid: authInfo?.xid,
        commerceIndicator: authInfo?.indicator,  // 'vbv' | 'spa' | 'aesk'
        ucafCollectionIndicator: authInfo?.ucafCollectionIndicator,
        ucafAuthenticationData: authInfo?.ucafAuthenticationData,
        directoryServerTransactionId: authInfo?.directoryServerTransactionId,
        paSpecificationVersion: authInfo?.paSpecificationVersion || authInfo?.specificationVersion,
        acsTransactionId: authInfo?.acsTransactionId,
        authenticationTransactionId: authInfo?.authenticationTransactionId,
        strongAuthentication: authInfo?.strongAuthentication as Record<string, string> | undefined
      };

      // Step 6: Process Final Payment with full 3DS auth data
      const paymentResponse = await PortalApiService.processFinalPayment(paymentInfo, authData);

      if (!['AUTHORIZED', 'AUTHORIZED_PENDING_REVIEW'].includes(paymentResponse.status)) {
        throw new Error(paymentResponse.message || `Payment failed: ${paymentResponse.status}`);
      }

      if ((window as any).__portal3DSResolve) {
        (window as any).__portal3DSResolve({
          success: true,
          paymentId: paymentResponse.id,
          authCode: paymentResponse.authCode || paymentResponse.processorInformation?.approvalCode
        });
        delete (window as any).__portal3DSResolve;
      }
    } catch (error: any) {
      console.error('[3DS] validation/payment error:', error);
      if ((window as any).__portal3DSResolve) {
        (window as any).__portal3DSResolve({
          success: false,
          error: error.message || '3DS authentication or payment failed'
        });
        delete (window as any).__portal3DSResolve;
      }
    }
  };

  // Handle 3DS error
  const handle3DSError = (error: string) => {
    setShow3DSModal(false);
    setThreeDSData(null);
    setPaymentError(error);
    setView(View.PAYMENT);

    if (threeDSFlow === 'booking') {
      if ((window as any).__bookingPaymentResolve) {
        (window as any).__bookingPaymentResolve({ success: false, error });
        delete (window as any).__bookingPaymentResolve;
      }
      delete (window as any).__bookingPaymentContext;
      return;
    }

    if ((window as any).__portal3DSResolve) {
      (window as any).__portal3DSResolve({
        success: false,
        error
      });
      delete (window as any).__portal3DSResolve;
    }
  };

  // SAM-199 — submit a SERVICE booking via POST /bookings/payment/orchestrate, entirely
  // SEPARATE from handleConfirm's product/business_access path below (which stays byte-identical
  // for a product sale — this function is never called for one). Does NOT call
  // useCreateInvitationOrAccess / business_access: the backend grants the gate QR itself as part
  // of confirming the booking (see serviceBookingOrchestration.ts's module docstring for what was
  // verified) — calling it again here would create a second, unlinked access.
  const handleServiceBookingConfirm = async () => {
    const selection = invitation.serviceBooking;
    if (!selection) return;

    if (!bookingBusiness.id) {
      NotificationService.error("Business information not available");
      return;
    }

    // SAM-198 «Existing client»: the account already has its phone; on_behalf_of sends only its id.
    const existingUserId = invitation?.guests?.mainGuest?.userId ?? null;
    const rawGuestPhone = invitation?.guests?.mainGuest?.phoneNumber || '';
    const e164Phone = formatToE164(rawGuestPhone);
    const phoneDigitCount = e164Phone.replace(/\D/g, '').length;
    if (existingUserId === null && (!isValidE164(e164Phone) || phoneDigitCount < 8 || phoneDigitCount > 15)) {
      NotificationService.error(
        "Enter a valid phone number in international format, e.g. +1 809 555 1234"
      );
      return;
    }

    setIsLoading(true);
    setPaymentError(null);

    // SAM-199: reuse the SAME idempotency key across retries of this attempt (see
    // getOrCreateIdempotencyKey's docstring) — persisted on the invitation so a network error or
    // a declined card and a subsequent click of Confirm resubmits under the identical key instead
    // of minting a new one every attempt.
    const idempotencyKey = getOrCreateIdempotencyKey(invitation.serviceBookingIdempotencyKey);
    if (idempotencyKey !== invitation.serviceBookingIdempotencyKey) {
      updateInvitation({ serviceBookingIdempotencyKey: idempotencyKey });
    }

    try {
      const onBehalfOf = buildOnBehalfOf({
        mainGuest: {
          firstName: invitation.guests?.mainGuest?.firstName,
          lastName: invitation.guests?.mainGuest?.lastName,
          phoneNumber: e164Phone,
          userId: existingUserId,
        },
        companions: invitation.guests?.companions?.companions,
        childrenCount: invitation.guests?.companions?.childrens,
      });

      // A card was entered — run the SAME 3DS chain a guest's own paid booking would, before
      // ever posting to orchestrate. Left completely blank -> submit with no card/auth_data and
      // let the server decide: a genuinely free selection confirms immediately; a paid one comes
      // back 400 (see mapOrchestrationError's 'missing_3ds' case) and the operator is routed back
      // here to add a card and retry under the SAME idempotency key.
      const creditCard = invitation.creditCard;
      const hasCardInput = Boolean(creditCard?.cardNumber || creditCard?.cardHolder || creditCard?.expiryDate || creditCard?.cvv);

      let payment: { card: { number: string; expiration_month: string; expiration_year: string; security_code: string }; authData?: OrchestrateAuthData } | undefined;

      if (hasCardInput) {
        if (!creditCard || creditCard.cardNumber?.length !== 16 || creditCard.cardHolder?.length < 3 ||
            !creditCard.expiryDate?.match(/^\d{2}\/\d{2}$/) || (creditCard.cvv?.length || 0) < 3) {
          NotificationService.error("Enter a complete card, or leave the card fields blank for a free booking");
          setIsLoading(false);
          return;
        }

        // The server prices the booking — we do not know the real amount yet. Pass a
        // placeholder > 0 (1.00) purely to drive the 3DS SETUP/ENROLL calls' own required
        // `amount` field; the CHARGE amount is decided by orchestrate_booking_payment server-side
        // from service.booking_options and is never taken from this value. Cardinal's device
        // fingerprinting/enrollment check does not bind the eventual charge to this figure.
        const [expiryMonth, expiryYear] = creditCard.expiryDate.split('/');
        const authResult = await processServiceBookingAuth({
          serviceId: selection.serviceId,
          amount: 1,
          currency: 'DOP',
        });

        if (!authResult.success) {
          setPaymentError(authResult.error || '3DS authentication failed');
          setView(View.PAYMENT);
          setIsLoading(false);
          return;
        }

        payment = {
          card: {
            number: creditCard.cardNumber,
            expiration_month: expiryMonth,
            expiration_year: `20${expiryYear}`,
            security_code: creditCard.cvv,
          },
          authData: authResult.authData,
        };
      }

      const payload = buildOrchestratePayload({
        selection,
        onBehalfOf,
        idempotencyKey,
        payment,
      });

      const response: any = await directApiInstance({
        url: '/bookings/payment/orchestrate',
        method: 'POST',
        data: payload,
      });

      const bookingData = response?.data || {};

      // SAM-542's 202 responses (payment authorized but confirm failed -> needs_recovery, or a
      // replay of an in-flight `payment_authorized`/`needs_recovery` row) are a 2xx status, so
      // axios resolves them — they never reach the `catch` block below. httpClient's response
      // interceptor also strips the actual HTTP status code off the resolved value (it returns
      // only `response.data`), so a 202 is indistinguishable from a 201 by status alone here. The
      // one thing SAM-542's response shapes never share is `booking_id`: only a real STATE_CONFIRMED
      // response (`_confirm_and_record`'s 201, or a replay of one) has it. Its absence means the
      // booking is NOT actually confirmed — treating it as success here would show the operator a
      // QR/access that doesn't exist yet. Route it through the exact same error mapping as any
      // other non-happy-path response instead.
      if (!bookingData.booking_id) {
        throw { response: { status: 202, data: { message: response?.message } } };
      }

      setCreatedInvitationData({
        id: bookingData.access?.qr_code || bookingData.booking_id,
        code: bookingData.access?.qr_code || bookingData.booking_id,
        business_access_status_id: bookingData.access?.status_id,
        start_date: bookingData.access?.start_date || bookingData.reservation_start,
        end_date: bookingData.access?.end_date || bookingData.reservation_end,
        business_name: bookingBusiness.name,
        main_guest: onBehalfOf.main_guest ?? {
          name: invitation.guests?.mainGuest?.firstName || '',
          last_name: invitation.guests?.mainGuest?.lastName || '',
          phone_number: invitation.guests?.mainGuest?.phoneNumber || undefined,
        },
        business_guests: onBehalfOf.business_guests,
        children_count: onBehalfOf.children_count,
        entity: 'business_access',
        service_name: selection.serviceName,
        total_amount: bookingData.total_amount,
        currency: bookingData.currency,
        payment_status: bookingData.payment_status,
      });

      updateInvitation({
        legacy_code: undefined,
        code: bookingData.access?.qr_code || bookingData.booking_id,
        business_name: bookingBusiness.name,
        // Clear the idempotency key on success — a follow-up submit from this page (there
        // shouldn't be one; resetInvitation runs on entering the page again) must never reuse a
        // key that already reached STATE_CONFIRMED.
        serviceBookingIdempotencyKey: null,
      });

      setSuccessKind('booking');
      setShowSuccessModal(true);
      setView(View.COMPLETED);
    } catch (error: any) {
      const mapped = mapOrchestrationError(error);
      // Only the mapped reason — never the raw error. An axios failure carries `config.data`,
      // which for this request is the body we just sent, card number and CVV included; logging it
      // would put a PAN in the browser console.
      console.error('Error submitting service booking:', mapped.code, mapped.message);
      setPaymentError(mapped.message);
      setView(View.PAYMENT);
    } finally {
      setIsLoading(false);
    }
  };

  // SAM-1171 (Security): one create at a time. A double click would otherwise create two passes, two links and two SMS.
  const confirmInFlight = useRef(false);
  const handleConfirm = () => runIfIdle(confirmInFlight, submitConfirm);

  const submitConfirm = async () => {
    setSubmitError(null);
    // SAM-199: a SERVICE booking takes a completely separate submit path — never the
    // product/business_access flow below, which stays byte-identical to how it behaved before
    // this ticket.
    if (invitation?.serviceBooking) {
      return handleServiceBookingConfirm();
    }

    try {
      if (invitation) {
        // The business is checked below, once the endpoint is known: the tenant Front Desk has no
        // business of its own and must still be able to create residential invitations.

        // Obtener el username del usuario actual (desde Cognito)
        let username = 'Admin'; // Valor por defecto
        try {
          const currentUser = await getCurrentUser();
          await fetchUserAttributes();
          username = currentUser.username || 'Admin';
        } catch (error) {
          console.error('Error obteniendo usuario actual:', error);
        }

        // Companions with what the guests step captured (SAM-842), or generic ones by count.
        const businessGuests = buildBusinessGuests(
          invitation.guests?.companions?.adults || 0,
          invitation.guests?.companions?.companions || [],
        );

        // The days the operator chose in «How long?»: one day, or a range (first day to the end of the last).
        const { startDate, endDate } = passWindow(invitation?.duration);

        setIsLoading(true);

        // Get the selected estate ID - always include for residential invitations
        const selectedEstateId = invitation?.location?.estate_id?.[0]?.id;
        const endpointType = sessionStorage.getItem('invitationEndpointType') || 'business_access';

        // Validation: For residential invitations, estate_id is required
        if (endpointType === 'invitation' && !selectedEstateId) {
          NotificationService.error("Please select a specific property for residential passes");
          setIsLoading(false);
          return;
        }

        // SAM-221: validate the main guest's phone before creating the
        // invitation. Impossible numbers (e.g. "+1 1") were being saved, so an
        // SMS/WhatsApp invitation could never reach the guest. Require a valid
        // E.164 number with a plausible length (8–15 digits). This is a client
        // guard; the backend must enforce the same rule for app/API callers.
        const rawGuestPhone = invitation?.guests?.mainGuest?.phoneNumber || '';
        const e164Phone = formatToE164(rawGuestPhone);
        const phoneDigitCount = e164Phone.replace(/\D/g, '').length;
        if (!isValidE164(e164Phone) || phoneDigitCount < 8 || phoneDigitCount > 15) {
          NotificationService.error(
            "Enter a valid phone number in international format, e.g. +1 809 555 1234"
          );
          setIsLoading(false);
          return;
        }

        // For business_access, the user can pick a different business from the destination picker
        // (EstateGroupFirstControl with kind='business'). When that selection has isBusiness=true,
        // use its id instead of the global context business — otherwise the access ends up
        // attached to the user's default/login business regardless of what they picked.
        const selectedDestination = invitation?.location?.estate_id?.[0] as any;
        const passBusiness = resolvePassBusiness({
          endpointType,
          destination: selectedDestination,
          contextBusinessId: business?.id ?? null,
          isTenantFrontDesk: picksPassBusiness(permissions),
        });
        if (!passBusiness.ok) {
          NotificationService.error("Business information not available");
          setIsLoading(false);
          return;
        }
        // SAM-1304: the pass of an event belongs to the event's business and carries its id (the server re-checks both)
        const eventPass = invitation?.selectedEvent ?? null;
        const resolvedBusinessId = eventPass?.business_id ?? passBusiness.businessId;

        // Build invitation data object
        const invitationData: any = {
          business_id: resolvedBusinessId,
          event_id: eventPass?.id ?? null,
          // SAM-199: `services_id: 3` removed — the DB column is `service_id` (uuid FK), not
          // `services_id`, and businessAccess/lambda_function.py's `_normalize_service_id` only
          // keeps a `services_id`/`service_id` value when it parses as a UUID (SAM-461). The
          // literal integer 3 never did, so it was silently dropped server-side on every
          // request — this line has never had any effect. Verified against
          // sam-lambda-functions functions/businessAccess/lambda_function.py.
          id_channel: 1,
          created_by: username,
          modified_by: null,
          business_access_type_id: resolveBusinessAccessTypeId({
            picked: invitation?.accessType?.[0],
            isTenantFrontDesk: picksPassBusiness(permissions),
          }),
          start_date: startDate,
          end_date: endDate,
          business_access_status_id: 1,
          children_count: invitation?.guests?.companions?.childrens || 0,
          main_guest: {
            name: invitation?.guests?.mainGuest?.firstName || 'Guest',
            last_name: invitation?.guests?.mainGuest?.lastName || '',
            title_es: 'Principal',
            tel: invitation?.guests?.mainGuest?.phoneNumber || '',
            // Document scan fields, photo included (SAM-842) — null if not scanned (retro-compatible)
            ...scanFields(invitation?.guests?.mainGuest),
            // SAM-253: 'share_link' means no ID document was captured here —
            // the guest completes it themselves via the SMS link the backend
            // already sends automatically on invitation creation. Forwarded
            // so the backend can key off it if/when it distinguishes the two
            // flows; today creation alone triggers that SMS either way.
            capture_mode: invitation?.guests?.mainGuest?.captureMode || 'manual',
          },
          business_guests: businessGuests
        };

        // SAM-1171: a pass_fee service (SAM-1184) is paid by the guest through a payment link. The server prices it,
        // charges it through the business's Cardnet pair on /pay/{token} and sends the link by SMS; no card here.
        if (isPassFeeService(invitation?.selectedService)) {
          invitationData.service_id = invitation!.selectedService!.id;
          invitationData.payment_link = true;
        }

        // For residential (invitation endpoint), always include estate_id
        // For business (business_access endpoint), estate_id will be excluded in the mutation hook
        if (endpointType === 'invitation') {
          invitationData.estate_id = selectedEstateId;
        }

        // Use the combined hook that automatically chooses the correct endpoint
        const response = await createInvitation(invitationData);

        // Handle different response formats from combined hook
        let invitationResponse: any;
        if ((response as any)?.data?.[0]) {
          // business_access endpoint response
          invitationResponse = (response as any).data[0];
        } else if ((response as any)?.id) {
          // invitation endpoint response
          invitationResponse = response;
        } else if (response) {
          // fallback
          invitationResponse = response;
        }

        if (invitationResponse) {
          // Store the complete invitation response data. SAM-1171: a pass paid by link also carries the link
          // (pay_url, amount, expiry) and whether its SMS went out, both at the top level of the response.
          setCreatedInvitationData((response as any)?.payment_link
            ? { ...invitationResponse, payment_link: (response as any).payment_link, payment_link_sms: (response as any).notifications?.sms }
            : invitationResponse);

          // SAM-307: the create response carries the door PIN status. A failure is told to
          // the operator now instead of being found by the guest at the door; the
          // confirmation view also shows it (and "generating" while pending).
          const hikPin = readHikPin(invitationResponse);
          if (hikPin.status === 'failed') {
            NotificationService.warning(hikPinFailureMessage(hikPin), { autoHideDuration: 10000 });
          }

          updateInvitation({
            legacy_code: invitationResponse.legacy_code,
            code: invitationResponse.id,
            business_name: invitationResponse.business_name || bookingBusiness.name || (invitation?.location?.estate_id?.[0] as any)?.label
          });

          // Note: SMS is handled automatically by backend based on invitation data
          // No need to send SMS manually from frontend

          // Mostrar el modal de éxito y cambiar a la vista COMPLETED
          setSuccessKind('pass');
          setShowSuccessModal(true);
          setView(View.COMPLETED);
        }
      }
    } catch (error: any) {
      // Status and code only: the response body carries the guest's phone and document.
      console.error('Error creando invitación:', error?.response?.status, error?.response?.data?.errorCode ?? error?.code);

      if (error?.response?.status === 400 &&
        (error?.response?.data?.errorCode === 'PAYMENT_ERROR' ||
          error?.response?.data?.errorCode === 'PAYMENT_FAILED')) {
        // Set payment error state (will be displayed in PaymentStep)
        setPaymentError("Error processing payment. Please try another payment method.");

        // Return to payment step
        setView(View.PAYMENT);
      } else {
      // Generic error
        setSubmitError(getApiErrorMessage(error, 'The pass could not be created. Try again.'));
        NotificationService.error("Error creating pass");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const requiresPayment = invitation?.accessType?.[0]?.label === 'Client Booking' &&
    Boolean(
      (invitation?.product?.[0]?.price && invitation.product[0].price > 0) ||
      // SAM-199: a service booking always routes through the Payment view — its price isn't
      // known client-side (see CLAUDE.md "Never send a price from the client"), so whether a
      // card is actually needed is left to the server. The card field itself is optional there
      // (see PaymentStep's cardOptional prop) so a genuinely free service can still continue.
      invitation?.serviceBooking
    );

  // SAM-199 second pass — "Access" (was "Dates"): the reorder folded product/service
  // selection, "How long?" and (for a service) time+quantities all into this one View.FORM
  // step, so "Dates" undersold what it covers. The view sequence itself is unchanged — this
  // is a label fix, not a new step.
  const steps = requiresPayment ? [
    { label: "Access", completed: view !== View.FORM },
    { label: "Guests & Scan", completed: view === View.PAYMENT || view === View.DETAIL || view === View.COMPLETED },
    { label: "Payment", completed: view === View.DETAIL || view === View.COMPLETED },
    { label: "Confirm", completed: view === View.COMPLETED },
    { label: "Send", completed: view === View.COMPLETED }
  ] : [
    { label: "Access", completed: view !== View.FORM },
    { label: "Guests", completed: view === View.SCAN || view === View.DETAIL || view === View.COMPLETED },
    { label: "Confirm", completed: view === View.COMPLETED },
    { label: "Send", completed: view === View.COMPLETED }
  ];

  const currentStep = (() => {
    if (requiresPayment) {
      switch (view) {
        case View.FORM:
          return 0;
        case View.SCAN:
          return 1;
        case View.PAYMENT:
          return 2;
        case View.DETAIL:
          return 3;
        case View.COMPLETED:
          return 4;
        default:
          return 0;
      }
    } else {
      switch (view) {
        case View.FORM:
          return 0;
        case View.SCAN:
          return 1;
        case View.DETAIL:
          return 2;
        case View.COMPLETED:
          return 3;
        default:
          return 0;
      }
    }
  })();

  const handleStepClick = (index: number) => {
    switch (index) {
      case 0:
        setView(View.FORM);
        break;
      case 1:
        if (steps[0].completed) {
          setView(View.SCAN);
        }
        break;
      case 2:
        if (steps[1].completed) {
          setView(View.DETAIL);
        }
        break;
      default:
        break;
    }
  };

  const handleDoneFromConfirmation = () => {
    goToInvitations();
  };

  const renderContent = () => {
    switch (view) {
      case View.FORM:
        return <AccessForm onComplete={handleInvitationCreated} isLoading={isFormLoading} onExit={handleBack} />;
      case View.SCAN: {
        // Use local companions data instead of context
        const companions = companionsData.adults;

        // Create companions list with proper names
        const companionsList: Guest[] = companions >= 2
          ? Array(companions - 1).fill({}).map((_, index) => {
              return {
                firstName: `Guest ${index + 2}`,
                lastName: ""
              };
            })
          : [];

        return (
          <DocumentScanForm
            mainGuest={{
              firstName: invitation?.guests?.mainGuest?.firstName || "Main Guest",
              lastName: invitation?.guests?.mainGuest?.lastName || "",
            }}
            companions={companionsList}
            onComplete={handleDocumentsScan}
            onSendInvitation={handleSendInvitation}
            onBack={() => setView(View.FORM)}
            onSetView={(newView, invitationData) => {
              if (invitationData) {
                setCreatedInvitationData(invitationData);
              }
              setView(View[newView as keyof typeof View]);
            }}
          />
        );
      }
      case View.PAYMENT:
      case View.DETAIL:
        // SAM-944: Payment and Confirm are one screen on a phone (Review and pay).
        if (isPhone) {
          return (
            <PhoneReviewPage
              requiresPayment={Boolean(requiresPayment)}
              cardOptional={Boolean(invitation?.serviceBooking)}
              error={paymentError}
              isLoading={isLoading}
              onConfirm={handleConfirm}
              onBack={() => setView(View.SCAN)}
              onClose={handleBack}
            />
          );
        }
        return view === View.PAYMENT ? (
          <PaymentStep
            onNext={() => setView(View.DETAIL)}
            onBack={() => setView(View.SCAN)}
            isLoading={isLoading}
            error={paymentError}
            onClearError={() => {
              setPaymentError(null);
            }}
            cardOptional={Boolean(invitation?.serviceBooking)}
          />
        ) : (
          <InvitationDetail
            onConfirm={handleConfirm}
            onCancel={handleBack}
            disableConfirm={isLoading}
            isLoading={isLoading}
            error={submitError}
          />
        );
      case View.COMPLETED:
        return (
          <ConfirmationView
            onClose={handleDoneFromConfirmation}
            invitationData={createdInvitationData}
            phoneStep={isPhone}
          />
        );
      default:
        return null;
    }
  };

  const showSummaryRail = view === View.SCAN || view === View.PAYMENT || view === View.DETAIL;

  const overlays = (
    <>
      <SuccessModal
        isOpen={showSuccessModal}
        kind={successKind}
        onClose={() => {
          setShowSuccessModal(false);
          setView(View.COMPLETED);
        }}
      />
      {threeDSData && (
        <ThreeDSModal
          isOpen={show3DSModal}
          stepUpUrl={threeDSData.stepUpUrl}
          accessToken={threeDSData.accessToken}
          authTransactionId={threeDSData.authTransactionId}
          onAuthenticationComplete={handle3DSComplete}
          onError={handle3DSError}
        />
      )}
    </>
  );

  // SAM-944: on a phone every step is a full-screen wizard page with its own header and progress bar.
  if (isPhone) {
    return <div className="flex h-full flex-col bg-white">{renderContent()}{overlays}</div>;
  }

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--surface)' }}>
      {/* ======================== PAGE HEADER ========================
          Same shell idiom as ServiceEditorPage's header: back arrow, round
          icon badge, title + subtitle. */}
      <header
        className="flex items-center gap-3 px-4 py-2 sm:px-6 sm:py-4 shrink-0 bg-white"
        style={{ borderBottom: '1px solid var(--line)' }}
      >
        <button
          type="button"
          onClick={handleBack}
          className="ico-btn"
          aria-label="Back to Passes"
          style={{
            width: 32, height: 32, borderRadius: 'var(--r-8)', border: '1px solid var(--line)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#fff',
            color: 'var(--ink-700)', flexShrink: 0,
          }}
        >
          <ArrowLeft size={16} />
        </button>

        <div
          className="hidden sm:flex"
          style={{
            width: 40, height: 40, borderRadius: '50%', background: 'var(--tenant-primary, #111827)',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          <UserPlus size={18} color="#fff" />
        </div>

        <div className="min-w-0 flex-1">
          <h1 className="text-[17px] font-semibold truncate" style={{ color: 'var(--ink-900)' }}>
            New pass
          </h1>
          <p className="hidden sm:block text-xs mt-0.5 truncate" style={{ color: 'var(--ink-500)' }}>
            Give a guest a pass
          </p>
        </div>
      </header>

      {/* ======================== BODY: progress bar + content ========================
          ProgressBar stays at the top of the content (unchanged component/props). The
          BookingSummaryRail sits in a second column at >=1024px and stacks below the
          step content on narrower viewports — same component, only re-homed from the
          old drawer's flex row into this page's two-column layout. At >=1024px the rail
          is `position: sticky` (same "stay visible while the rest scrolls" idea as
          ServiceEditorPage's sticky save bar, just top-pinned here instead of
          bottom-pinned): it sticks just under the page header — which sits outside this
          scroll container, so `top: 16` alone is enough — while the (often much longer)
          guest/payment/confirm form scrolls past it. `alignSelf: 'flex-start'` keeps its
          height to its own content instead of the flex row stretching it to match the
          main column, which would leave sticky with nowhere to go. Stacked below on
          narrower viewports, it must NOT stick — pinning it there would cover the form. */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-5xl mx-auto p-6">
          <ProgressBar
            steps={steps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
          />

          <div className="flex flex-col lg:flex-row gap-6 mt-2">
            <div className="flex-1 min-w-0">{renderContent()}</div>
            {showSummaryRail && (
              <div
                className="w-full lg:w-auto flex-shrink-0 lg:sticky"
                style={{ top: 16, alignSelf: 'flex-start' }}
              >
                <BookingSummaryRail />
              </div>
            )}
          </div>
        </div>
      </div>

      {overlays}
    </div>
  );
};

export default AccessPage;
